import type {
  Movement,
  TastingStatus,
  Wine,
  WineCategory,
  WineFields,
  CellarLocation,
  CellarPlace,
  CompletionField,
  WineLocationStock,
  CoravinBottle,
} from "../types";

const CATEGORY_ALIASES: Record<string, WineCategory> = {
  rouge: "Rouge",
  blanc: "Blanc",
  rose: "Rosé",
  rosé: "Rosé",
  orange: "Orange",
  effervescent: "Effervescent",
  doux: "Liquoreux",
  liquoreux: "Liquoreux",
  "doux / liquoreux": "Liquoreux",
  "doux / liquoreux (non fortifié)": "Liquoreux",
  doux_liquoreux: "Liquoreux",
  doux_liquoreux_non_fortifie: "Liquoreux",
  fortifie: "Vin fortifié / vin doux naturel",
  fortifié: "Vin fortifié / vin doux naturel",
  "vin fortifié / vin doux naturel": "Vin fortifié / vin doux naturel",
  "vin fortifie / vin doux naturel": "Vin fortifié / vin doux naturel",
  vin_fortifie_vin_doux_naturel: "Vin fortifié / vin doux naturel",
  autre: "Autre",
};

const MOVEMENT_ALIASES: Record<string, Movement["motif"]> = {
  stock_initial: "Stock initial",
  "stock initial": "Stock initial",
  achat: "Achat",
  bouteille_bue: "Bouteille bue",
  "bouteille bue": "Bouteille bue",
  correction: "Correction",
  transfert: "Transfert",
  transfer: "Transfert",
};

const clean = (value: unknown): string | null => {
  if (typeof value !== "string") return value == null ? null : String(value);
  const trimmed = value.trim();
  return trimmed || null;
};

const numberOrNull = (value: unknown): number | null => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function normalizeCategory(value: unknown): WineCategory {
  const normalized = String(value ?? "Autre").trim().toLocaleLowerCase("fr");
  return CATEGORY_ALIASES[normalized] ?? "Autre";
}

export function normalizeMovement(raw: Record<string, unknown>): Movement {
  const rawReason = String(raw.motif ?? raw.motif_code ?? raw.reason ?? "Correction");
  return {
    id: String(raw.id ?? raw.mouvement_id ?? ""),
    vin_id: String(raw.vin_id ?? raw.wine_id ?? ""),
    quantite: Number(raw.quantite ?? raw.quantity ?? 0),
    motif:
      MOVEMENT_ALIASES[rawReason.trim().toLocaleLowerCase("fr")] ??
      (rawReason as Movement["motif"]),
    date_effective: clean(raw.date_effective ?? raw.effective_date),
    date_creation:
      clean(raw.date_creation ?? raw.created_at) ?? new Date().toISOString(),
    date_modification: clean(raw.date_modification ?? raw.updated_at),
    mouvement_annule_id: clean(
      raw.mouvement_annule_id ??
        raw.cancelled_movement_id ??
        raw.cancels_movement_id,
    ),
    prix_unitaire: numberOrNull(raw.prix_unitaire ?? raw.unit_price),
    vendeur: clean(raw.vendeur ?? raw.seller),
    note: clean(raw.note),
    emplacement_id: clean(raw.emplacement_id ?? raw.location_id),
    emplacement: clean(
      raw.emplacement_libelle ?? raw.emplacement ?? raw.location_label ?? raw.location,
    ),
    lieu_id: clean(raw.lieu_id ?? raw.place_id),
    lieu: clean(raw.lieu ?? raw.place),
    transfert_id: clean(raw.transfert_id ?? raw.transfer_id),
    ...(raw.coravin_id !== undefined ? { coravin_id: clean(raw.coravin_id) } : {}),
  };
}

export function normalizeCoravin(raw: Record<string, unknown>): CoravinBottle {
  return {
    id: String(raw.id ?? ""),
    vin_id: String(raw.vin_id ?? ""),
    emplacement_id: clean(raw.emplacement_id),
    emplacement: clean(raw.emplacement_libelle ?? raw.emplacement),
    lieu_id: clean(raw.lieu_id),
    lieu: clean(raw.lieu),
    date_coravin: String(raw.date_coravin ?? ""),
    date_fin: clean(raw.date_fin),
    note: clean(raw.note),
    mouvement_fin_id: clean(raw.mouvement_fin_id),
    date_creation: String(raw.date_creation ?? ""),
    date_modification: String(raw.date_modification ?? ""),
  };
}

/** Sans emplacementId, renvoie toute la fiche ; null cible le stock non placé. */
export function activeCoravins(
  wine: Pick<Wine, "coravins">,
  emplacementId?: string | null,
): CoravinBottle[] {
  return (wine.coravins ?? []).filter(
    (bottle) =>
      !bottle.date_fin &&
      (emplacementId === undefined || bottle.emplacement_id === emplacementId),
  );
}

function normalizeLocationStock(raw: Record<string, unknown>): WineLocationStock {
  const lieu = clean(raw.lieu ?? raw.place);
  const emplacementNom = clean(
    raw.emplacement_nom ?? raw.location_name ?? raw.nom,
  );
  return {
    emplacement_id: clean(raw.emplacement_id ?? raw.location_id),
    lieu_id: clean(raw.lieu_id ?? raw.place_id),
    lieu,
    emplacement: clean(
      raw.emplacement_libelle ??
        raw.emplacement ??
        raw.location_label ??
        (emplacementNom && lieu ? `${lieu} - ${emplacementNom}` : emplacementNom),
    ),
    emplacement_nom: emplacementNom,
    stock: Number(raw.stock ?? raw.quantite ?? raw.quantity ?? 0),
  };
}

export function normalizeWine(raw: Record<string, unknown>): Wine {
  const rawMovements = Array.isArray(raw.mouvements)
    ? raw.mouvements
    : Array.isArray(raw.movements)
      ? raw.movements
      : undefined;
  const mouvements = rawMovements?.map((movement) =>
    normalizeMovement(movement as Record<string, unknown>),
  );
  const calculatedStock = mouvements?.reduce(
    (total, movement) => total + movement.quantite,
    0,
  );
  const rawLocation =
    raw.emplacement && typeof raw.emplacement === "object"
      ? (raw.emplacement as Record<string, unknown>)
      : null;
  const rawPlace = clean(raw.lieu ?? rawLocation?.lieu);
  const lieu: CellarPlace | null = rawPlace;
  const rawStocks = Array.isArray(raw.stocks_par_emplacement)
    ? raw.stocks_par_emplacement
    : Array.isArray(raw.location_stocks)
      ? raw.location_stocks
      : null;
  const stock = Number(raw.stock ?? raw.quantite_disponible ?? calculatedStock ?? 0);
  const stocksParEmplacement = rawStocks
    ? rawStocks.map((item) =>
        normalizeLocationStock(item as Record<string, unknown>),
      )
    : stock !== 0 || raw.emplacement_id != null || rawLocation
      ? [
          {
            emplacement_id: clean(raw.emplacement_id ?? rawLocation?.id),
            lieu_id: clean(raw.lieu_id ?? rawLocation?.lieu_id),
            lieu,
            emplacement: clean(
              raw.emplacement_libelle ??
                rawLocation?.libelle ??
                (rawLocation
                  ? rawLocation.nom && lieu
                    ? `${lieu} - ${String(rawLocation.nom)}`
                    : rawLocation.nom
                  : raw.emplacement ?? raw.location),
            ),
            emplacement_nom: clean(rawLocation?.nom ?? raw.emplacement_nom),
            stock,
          },
        ]
      : [];

  return {
    id: String(raw.id ?? raw.vin_id ?? ""),
    producteur: clean(raw.producteur ?? raw.producer) ?? "Producteur inconnu",
    nom: clean(raw.nom ?? raw.name) ?? "Vin sans nom",
    appellation: clean(raw.appellation),
    region: clean(raw.region ?? raw.region_pays ?? raw.country_region),
    cepages: clean(raw.cepages ?? raw.grape_varieties),
    vinification_elevage: clean(raw.vinification_elevage),
    millesime: numberOrNull(raw.millesime ?? raw.vintage),
    categorie: normalizeCategory(raw.categorie ?? raw.category),
    format: clean(raw.format) ?? "75 cl",
    emplacement_id: clean(raw.emplacement_id ?? rawLocation?.id),
    emplacement: clean(
      raw.emplacement_libelle ??
        rawLocation?.libelle ??
        (rawLocation
          ? rawLocation.nom && lieu
            ? `${lieu} - ${String(rawLocation.nom)}`
            : rawLocation.nom
          : raw.emplacement ?? raw.location),
    ),
    lieu_id: clean(raw.lieu_id ?? rawLocation?.lieu_id),
    lieu,
    debut_deg: numberOrNull(
      raw.debut_deg ?? raw.debut_degustation ?? raw.drinking_start,
    ),
    fin_deg: numberOrNull(raw.fin_deg ?? raw.fin_degustation ?? raw.drinking_end),
    note: clean(raw.note ?? raw.note_vin),
    date_creation: clean(raw.date_creation ?? raw.created_at) ?? undefined,
    ...(clean(raw.date_modification ?? raw.updated_at)
      ? { date_modification: clean(raw.date_modification ?? raw.updated_at) ?? undefined }
      : {}),
    stock,
    stocks_par_emplacement: stocksParEmplacement,
    a_une_note_degustation: Boolean(
      raw.a_une_note_degustation ?? raw.has_tasting_note ?? false,
    ),
    dernier_prix_achat: numberOrNull(
      raw.dernier_prix_achat ?? raw.last_purchase_price,
    ),
    mouvements,
    ...(Array.isArray(raw.coravins)
      ? { coravins: raw.coravins.map((bottle) => normalizeCoravin(bottle as Record<string, unknown>)) }
      : {}),
  };
}

/** Retourne toujours une ventilation exploitable, y compris pour les anciennes fiches. */
export function wineLocationStocks(
  wine: Pick<
    Wine,
    | "stock"
    | "stocks_par_emplacement"
    | "emplacement_id"
    | "emplacement"
    | "lieu_id"
    | "lieu"
  >,
): WineLocationStock[] {
  if (wine.stocks_par_emplacement?.length) {
    return wine.stocks_par_emplacement;
  }
  if (
    wine.stock === 0 &&
    !wine.emplacement_id &&
    !wine.emplacement &&
    !wine.lieu_id &&
    !wine.lieu
  ) {
    return [];
  }
  return [
    {
      emplacement_id: wine.emplacement_id,
      lieu_id: wine.lieu_id,
      lieu: wine.lieu,
      emplacement: wine.emplacement,
      emplacement_nom: wine.emplacement,
      stock: wine.stock,
    },
  ];
}

export function normalizeLocation(raw: Record<string, unknown>): CellarLocation {
  const place = clean(raw.lieu);
  const lieu: CellarPlace | null = place;
  const nom = clean(raw.nom ?? raw.name) ?? "Emplacement sans nom";
  const rawCapacity = numberOrNull(
    raw.capacite_totale ?? raw.total_capacity ?? raw.capacity,
  );
  return {
    id: String(raw.id ?? raw.emplacement_id ?? ""),
    lieu_id: clean(raw.lieu_id ?? raw.place_id),
    lieu,
    nom,
    libelle:
      clean(raw.libelle ?? raw.label) ?? (lieu ? `${lieu} - ${nom}` : nom),
    capacite_totale:
      rawCapacity !== null && Number.isSafeInteger(rawCapacity) && rawCapacity >= 0
        ? rawCapacity
        : null,
    date_modification: clean(raw.date_modification ?? raw.updated_at) ?? undefined,
    nombre_fiches: Number(raw.nombre_fiches ?? raw.reference_count ?? 0),
    stock: Number(raw.stock ?? raw.bottle_count ?? 0),
  };
}

export function categoryApiValue(category: WineCategory): string {
  const values: Record<WineCategory, string> = {
    Rouge: "rouge",
    Blanc: "blanc",
    Rosé: "rose",
    Orange: "orange",
    Effervescent: "effervescent",
    Liquoreux: "doux_liquoreux",
    "Vin fortifié / vin doux naturel": "fortifie",
    Autre: "autre",
  };
  return values[category];
}

export function currentYear(): number {
  return Number(
    new Intl.DateTimeFormat("fr-FR", {
      timeZone: "Europe/Paris",
      year: "numeric",
    }).format(new Date()),
  );
}

export function getTastingStatus(
  wine: Pick<Wine, "debut_deg" | "fin_deg">,
  year = currentYear(),
): TastingStatus {
  const start = wine.debut_deg;
  const end = wine.fin_deg;

  if (start == null && end == null) return "Non renseigné";
  if (start != null && start > year) return "À garder";
  return "À boire dès maintenant";
}

export function tastingPriority(status: TastingStatus): number {
  if (status === "À boire dès maintenant") return 0;
  if (status === "À garder") return 1;
  return 2;
}

export function tastingPeriod(wine: Pick<Wine, "debut_deg" | "fin_deg">): string {
  if (wine.debut_deg != null && wine.fin_deg != null) {
    return `${wine.debut_deg} – ${wine.fin_deg}`;
  }
  if (wine.debut_deg != null) return `À partir de ${wine.debut_deg}`;
  if (wine.fin_deg != null) return `Jusqu’en ${wine.fin_deg}`;
  return "Non renseignée";
}

export function vintageLabel(wine: Pick<Wine, "millesime">): string {
  return wine.millesime == null ? "NM" : String(wine.millesime);
}

export function bottleLabel(quantity: number): string {
  return `${quantity} bouteille${Math.abs(quantity) === 1 ? "" : "s"}`;
}

export function movementLabel(movement: Pick<Movement, "motif" | "quantite">): string {
  if (movement.motif === "Bouteille bue") return "Bouteille bue";
  if (movement.motif === "Achat") return "Achat";
  if (movement.motif === "Stock initial") return "Stock initial";
  if (movement.motif === "Transfert") return "Transfert";
  return movement.quantite > 0 ? "Correction positive" : "Correction négative";
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "Date non renseignée";
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function normalizedKey(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("fr");
}

export function sameReference(
  candidate: Wine,
  values: {
    producteur: string;
    nom: string;
    appellation: string;
    millesime: number | null;
    categorie: string;
    format: string;
  },
): boolean {
  return (
    normalizedKey(candidate.producteur) === normalizedKey(values.producteur) &&
    normalizedKey(candidate.nom) === normalizedKey(values.nom) &&
    normalizedKey(candidate.appellation) === normalizedKey(values.appellation) &&
    candidate.millesime === values.millesime &&
    normalizedKey(candidate.categorie) === normalizedKey(values.categorie) &&
    normalizedKey(candidate.format) === normalizedKey(values.format)
  );
}

export function lastPurchasePrice(wine: Wine): number | null {
  const cancelledMovementIds = new Set(
    (wine.mouvements ?? []).flatMap((movement) =>
      movement.mouvement_annule_id ? [movement.mouvement_annule_id] : [],
    ),
  );
  const priced = (wine.mouvements ?? [])
    .filter(
      (movement) =>
        (movement.motif === "Achat" || movement.motif === "Stock initial") &&
        !movement.mouvement_annule_id &&
        !cancelledMovementIds.has(movement.id) &&
        movement.prix_unitaire != null,
    )
    .sort(
      (a, b) =>
        new Date(b.date_creation).getTime() - new Date(a.date_creation).getTime(),
    );
  return wine.mouvements
    ? priced[0]?.prix_unitaire ?? null
    : wine.dernier_prix_achat ?? null;
}

type EssentialPromptWine = Pick<
  WineFields,
  | "producteur"
  | "nom"
  | "millesime"
  | "categorie"
  | "appellation"
  | "region"
>;

export function buildWineCompletionPrompt(
  wine: EssentialPromptWine,
  selectedFields: readonly CompletionField[],
): string {
  const selected = [...new Set(selectedFields)];
  if (!selected.length) {
    throw new Error("Sélectionnez au moins une information à rechercher.");
  }

  const lines = [
    "Je souhaite compléter une fiche de vin à partir d’informations fiables.",
    "",
    "Vin connu :",
    `- ${wine.nom.trim()} — ${wine.producteur.trim()}, ${vintageLabel(wine)}`,
    `- Catégorie : ${wine.categorie}`,
  ];
  if (wine.appellation.trim()) lines.push(`- Appellation actuelle : ${wine.appellation.trim()}`);
  if (wine.region.trim()) lines.push(`- Région / pays actuel : ${wine.region.trim()}`);

  lines.push(
    "",
    "Méthode de recherche :",
    "- Effectue une recherche sur Internet avant de répondre ; ne te limite pas à tes connaissances générales.",
    "- Privilégie le site officiel du producteur, les fiches techniques et les organismes d’appellation.",
    "- Vérifie que chaque source concerne exactement le producteur, la cuvée et, lorsqu’il est indiqué, le millésime demandé.",
    "- Sans source publique suffisamment fiable, réponds « Introuvable ».",
    "",
    "Informations demandées :",
  );
  if (selected.includes("appellation")) {
    lines.push("- Appellation exacte, ou confirmation/correction de la valeur actuelle.");
  }
  if (selected.includes("region")) {
    lines.push("- Région et pays, ou confirmation/correction de la valeur actuelle.");
  }
  if (selected.includes("cepages")) {
    lines.push(
      "- Cépage(s) et, seulement lorsqu’elles sont publiées par une source fiable, leurs proportions. Ne déduis pas un assemblage de la seule appellation et signale toute variation possible selon le millésime.",
    );
  }
  if (selected.includes("periode")) {
    lines.push(
      "- Période de dégustation sous forme d’une plage d’années exploitable : année de début et année de fin.",
    );
  }
  if (selected.includes("prix")) {
    lines.push(
      "- Prix actuellement constaté par bouteille pour cette cuvée et ce millésime. Précise le format, la devise, le vendeur et la date de consultation ; si seul un autre millésime est disponible, signale-le clairement.",
    );
  }
  if (selected.includes("vinification_elevage")) {
    lines.push(
      "- Vinification et élevage : recherche uniquement des informations factuelles sur la méthode de vinification et les fermentations, le type et la durée d’élevage, les contenants utilisés (cuve, fût, amphore…), l’assemblage, le dosage et toute autre particularité technique utile. Présente chaque élément comme une proposition à valider avant saisie. Pour tout élément introuvable ou insuffisamment fiable, réponds « Introuvable » plutôt que de l’inventer.",
    );
  }

  lines.push(
    "",
    "Réponds de façon concise et structurée, avec un libellé par information demandée, afin que je puisse reporter manuellement les valeurs dans la fiche.",
    "Ajoute sous chaque information une ligne « Source : titre — URL ».",
    "Pour une période de dégustation, écris distinctement « Début : AAAA » et « Fin : AAAA ».",
    "Signale clairement toute incertitude ou information impossible à déterminer de façon fiable. N’invente rien.",
  );
  return lines.join("\n");
}

export interface PairingWine extends Wine {
  stock: number;
}

export function groupWinesForPairing(
  wines: readonly Wine[],
  place: CellarPlace,
  placeId?: string,
): PairingWine[] {
  const grouped = new Map<string, PairingWine>();
  wines
    .filter(
      (wine) =>
        wine.stock > 0 &&
        (placeId ? wine.lieu_id === placeId : wine.lieu === place),
    )
    .forEach((wine) => {
      const key = [
        wine.producteur,
        wine.nom,
        wine.appellation,
        wine.millesime ?? "NM",
        wine.categorie,
        wine.format,
      ]
        .map(normalizedKey)
        .join("|");
      const existing = grouped.get(key);
      if (existing) existing.stock += wine.stock;
      else grouped.set(key, { ...wine });
    });
  return [...grouped.values()].sort((a, b) =>
    `${a.producteur} ${a.nom}`.localeCompare(`${b.producteur} ${b.nom}`, "fr", {
      sensitivity: "base",
    }),
  );
}

export function buildPairingPrompt(
  wines: readonly Wine[],
  request: string,
  place: CellarPlace,
  placeId?: string,
): string {
  const available = groupWinesForPairing(wines, place, placeId);
  if (!request.trim()) throw new Error("Indiquez un plat ou une envie.");
  if (!available.length) {
    throw new Error(`Aucune bouteille n’est disponible à ${place}.`);
  }

  const lines = [
    "Je cherche le meilleur accord à partir des bouteilles que j’ai déjà.",
    `Plat ou envie : ${request.trim()}`,
    "",
    `Bouteilles disponibles à ${place} (choisis exclusivement dans cette liste) :`,
  ];
  available.forEach((wine, index) => {
    const details = [
      `${wine.producteur} — ${wine.nom}, ${vintageLabel(wine)}`,
      wine.categorie,
    ];
    if (wine.appellation) details.push(`appellation ${wine.appellation}`);
    if (wine.region) details.push(wine.region);
    if (wine.cepages) details.push(`cépage(s) ${wine.cepages}`);
    if (wine.debut_deg != null || wine.fin_deg != null) {
      details.push(`période ${tastingPeriod(wine)}`);
    }
    details.push(bottleLabel(wine.stock));
    lines.push(`${index + 1}. ${details.join(" | ")}`);
  });
  lines.push(
    "",
    "Propose au maximum trois bouteilles de cette liste, classées par pertinence, avec une courte explication pour chaque accord.",
    "Indique clairement ta recommandation préférée et, si utile, un conseil concret de température, d’ouverture ou de carafage.",
    "Si aucune bouteille ne convient de façon satisfaisante, dis-le honnêtement au lieu de forcer un accord.",
  );
  return lines.join("\n");
}
